import { ThemeProvider } from "@emotion/react";
import {
  Box,
  createTheme,
  Paper,
  Rating,
  Typography,
  // Box,
  Card,
  CardContent,
  CardMedia,
  // Typography,
  Chip,
  Stack,
  // Rating,
  useTheme,
} from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import MaleIcon from "@mui/icons-material/Male";
import FemaleIcon from "@mui/icons-material/Female";
import ReadIcon from "@mui/icons-material/AutoStories";
import WorkIcon from "@mui/icons-material/Work";
import GroupIcon from "@mui/icons-material/Group";
import makeFullUrl from "../../utils/cloudinary";
import PriceDisplay from "../PriceDisplay";

function PropertyCard({ property }) {
  // console.log(123, property);
  const theme = createTheme({
    components: {
      MuiTypography: {
        variants: [
          {
            props: { variant: "body2" },
            style: { fontSize: 11 },
          },
          {
            props: { variant: "body3" },
            style: { fontSize: 11 },
          },
        ],
      },
    },
  });

  // const bedrooms = `${
  //   property.bedrooms === "studio"
  //     ? "Studio"
  //     : property.bedrooms === "1"
  //       ? `${property.bedrooms} Bedroom`
  //       : `${property.bedrooms} Bedrooms`
  // }`;
  // const paymentFrequencyDisplay = {
  //   monthly: "Monthly",
  //   yearly: "Yearly",
  //   daily: "Daily",
  // };

  // const displayPaymentFrequency =
  //   paymentFrequencyDisplay[property.paymentFrequency] || "";

  const imageUrl = makeFullUrl(
    property.mainImage.filePath,
    property.mainImage.fileType,
  );

  const addressString = `${property.address?.area || ""}, ${
    property.address?.city?.name || ""
  }`;

  // Logic to determine Gender Icon/Color
  // You should ideally have a 'gender' field in your DB. Mocking for now:
  const getGenderBadge = (type) => {
    // Replace 'type' with property.gender or property.allowedGuests
    if (type === "female")
      return {
        label: "Women",
        color: "#cf5e93",
        // color: "pink",
        icon: <FemaleIcon fontSize="small" />,
      };
    if (type === "male")
      return {
        label: "Men",
        color: "info.main",
        icon: <MaleIcon fontSize="small" />,
      };
    return { label: "Family/All", color: "success.main", icon: null };
  };

  const getTenantTypeBadge = (type) => {
    // Replace 'type' with property.gender or property.allowedGuests
    if (type === "students")
      return {
        label: "Students",
        color: "success.main",
        // color: "pink",
        icon: <ReadIcon fontSize="small" />,
      };
    if (type === "professionals")
      return {
        label: "Professionals",
        color: "success.main",
        icon: <WorkIcon fontSize="small" />,
      };
    return {
      label: "Students & Professionals",
      color: "success.main",
      icon: <GroupIcon fontSize="small" />,
    };
  };

  const genderBadge = getGenderBadge(property.gender || "all"); // Default to all if undefined
  const tenantBadge = getTenantTypeBadge(property.tenantType || "all"); // Default to all if undefined

  // const formattedPrice = new Intl.NumberFormat("en-AE", {
  //   style: "currency",
  //   currency: "AED",
  //   minimumFractionDigits: property.minPrice % 1 !== 0 ? 2 : 0, // Ensures two decimal places if available
  //   maximumFractionDigits: 2, // Limits to two decimal places
  // }).format(property.minPrice);

  return (
    <>
      <Box sx={{ position: "relative" }}>
        <Paper elevation={3} sx={{ borderRadius: "10px" }}>
          <img src={imageUrl} alt="" className="img" />
        </Paper>
        {/* Top Left: Gender Badge */}
        <Chip
          label={genderBadge.label}
          icon={genderBadge.icon}
          size="small"
          variant="outlined"
          sx={{
            position: "absolute",
            top: 10,
            left: 10,
            bgcolor: "white",
            fontWeight: "bold",
            color: genderBadge.color,
            borderColor: genderBadge.color,
            "& .MuiChip-icon": { color: genderBadge.color },
          }}
        />
        {/* Top Right: Tenant Type Badge */}
        <Chip
          label={tenantBadge.label}
          icon={tenantBadge.icon}
          size="small"
          variant="outlined"
          sx={{
            position: "absolute",
            top: 10,
            right: 10,
            bgcolor: "white",
            fontWeight: "bold",
            color: tenantBadge.color,
            borderColor: tenantBadge.color,
            "& .MuiChip-icon": { color: tenantBadge.color },
            pl: 0.5,
          }}
        />
      </Box>

      <ThemeProvider theme={theme}>
        <Box padding={1}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="subtitle2" component="h2">
              {/* {property.address.city.name}, {property.address.state.name} */}
              {/* {addressString} */}
              {property.title || property.propertyType?.name}
            </Typography>
            <Chip
              label={property.propertyType?.name || "Apartment"}
              size="small"
              variant="outlined"
            />
          </Box>

          {/* 2. Location (Specific) */}
          <Box display="flex" alignItems="center" gap={0.5}>
            <LocationOnIcon
              sx={{
                fontSize: 18,
                color: "text.secondary",
                transform: "translateY(-1px)",
              }}
            />
            <Typography
              variant="subtitle2"
              component="h2"
              color="text.secondary"
              noWrap
            >
              {addressString}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center" }} marginTop={1}>
            <Rating
              name="read-only"
              value={property.rating}
              readOnly
              precision={0.5}
              size="small"
            />
            <Typography variant="body2" component="p" marginLeft={0.5}>
              {property.rating}
            </Typography>
            <Typography variant="body3" component="p" marginLeft={0.5}>
              {/* {property.numberOfReviews && `(${property.numberOfReviews})`} */}
              {property.reviewCount && `(${property.reviewCount})`}
            </Typography>
          </Box>
          <Box display="flex" alignItems="baseline">
            {property.unitCount > 1 && (
              <Typography
                variant="subtitle2"
                component="h2"
                sx={{ color: "text.secondary", mr: 0.5 }}
              >
                From
              </Typography>
            )}
            <Typography variant="subtitle2" component="h2">
              {/* {formattedPrice} {displayPaymentFrequency} */}
              <PriceDisplay
                unitPrice={property.minPrice}
                sx={{
                  fontSize: 15,
                  mt: 0.5,
                }}
                iconSize={15}
              />
            </Typography>
          </Box>
        </Box>
      </ThemeProvider>
    </>
  );

  // return (
  //   <Card
  //     sx={{
  //       maxWidth: 345,
  //       borderRadius: 3,
  //       boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
  //       transition: "transform 0.2s",
  //       "&:hover": { transform: "translateY(-4px)", boxShadow: 4 },
  //       position: "relative",
  //     }}
  //   >
  //     {/* IMAGE SECTION WITH OVERLAYS */}
  //     <Box sx={{ position: "relative" }}>
  //       <CardMedia
  //         component="img"
  //         height="200"
  //         image={imageUrl}
  //         alt={property.title}
  //         sx={{ objectFit: "cover" }}
  //       />

  //       {/* Top Left: Gender Badge */}
  //       <Chip
  //         label={genderBadge.label}
  //         icon={genderBadge.icon}
  //         size="small"
  //         sx={{
  //           position: "absolute",
  //           top: 10,
  //           left: 10,
  //           bgcolor: "white",
  //           fontWeight: "bold",
  //           color: genderBadge.color,
  //           "& .MuiChip-icon": { color: genderBadge.color },
  //         }}
  //       />

  //       {/* Top Right: Verified Badge */}
  //       {property.isVerified && (
  //         <Box
  //           sx={{
  //             position: "absolute",
  //             top: 10,
  //             right: 10,
  //             bgcolor: "rgba(0,0,0,0.6)",
  //             borderRadius: 1,
  //             p: 0.5,
  //             display: "flex",
  //           }}
  //         >
  //           <VerifiedUserIcon sx={{ color: "#4caf50", fontSize: 18 }} />
  //         </Box>
  //       )}
  //     </Box>

  //     {/* CONTENT SECTION */}
  //     <CardContent sx={{ pb: "16px !important" }}>
  //       {/* 1. Title & Type */}
  //       <Box
  //         display="flex"
  //         justifyContent="space-between"
  //         alignItems="flex-start"
  //         mb={0.5}
  //       >
  //         <Typography
  //           variant="subtitle1"
  //           fontWeight={700}
  //           noWrap
  //           sx={{ maxWidth: "70%" }}
  //         >
  //           {property.title || property.propertyType?.name}
  //         </Typography>
  //         <Box display="flex" alignItems="center" gap={0.5}>
  //           <Rating
  //             value={property.rating || 4.5}
  //             precision={0.5}
  //             size="small"
  //             readOnly
  //           />
  //           <Typography variant="caption" color="text.secondary">
  //             ({property.reviewCount || 0})
  //           </Typography>
  //         </Box>
  //       </Box>

  //       {/* 2. Location (Specific) */}
  //       <Box display="flex" alignItems="center" gap={0.5} mb={2}>
  //         <LocationOnIcon sx={{ fontSize: 16, color: "text.secondary" }} />
  //         <Typography variant="body2" color="text.secondary" noWrap>
  //           {addressString}
  //         </Typography>
  //       </Box>

  //       {/* 3. Divider / Specs */}
  //       {/* Optional: Add beds/baths here if space permits, or skip for cleaner look */}

  //       {/* 4. Price Footer */}
  //       <Box
  //         display="flex"
  //         justifyContent="space-between"
  //         alignItems="center"
  //         mt={1}
  //       >
  //         <Box>
  //           <Typography
  //             variant="caption"
  //             color="text.secondary"
  //             display="block"
  //           >
  //             Starting from
  //           </Typography>
  //           <PriceDisplay
  //             unitPrice={property.minPrice}
  //             sx={{
  //               fontSize: 18,
  //               fontWeight: "bold",
  //               color: theme.palette.primary.main,
  //             }}
  //             iconSize={16}
  //           />
  //         </Box>
  //         <Chip
  //           label={property.propertyType?.name || "Apartment"}
  //           size="small"
  //           variant="outlined"
  //         />
  //       </Box>
  //     </CardContent>
  //   </Card>
  // );
}

export default PropertyCard;
